import React from 'react';
import CardSection from '../../common/CardSection';

import User from '../../../models/User';
import FormTextbox, { FormTextBoxDataType } from 'src/components/controls/forms/redesign/FormTextbox';
import { FormErrors } from 'src/util/FormErrors';
import { connect } from 'react-redux';
import actions from 'src/actions';
import { replace } from 'connected-react-router';
import Button from 'src/components/controls/forms/redesign/FormButton';
import UserRole, { UserRoles } from 'src/models/UserRole';
import Form from 'src/components/controls/forms/Form';
import FormSelect from 'src/components/controls/forms/redesign/FormFilterSelect';
import { SelectItem } from 'src/models/common/SelectItem';
import { IRootState } from 'src/reducers';
import { AnyObject } from 'final-form';
import selectors from 'src/selectors';
import Office from 'src/models/Office';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { UserTypes } from 'src/models/UserType';
import { RouteComponentProps } from 'react-router-dom';
import FormTextBox from 'src/components/controls/forms/redesign/FormTextbox';
import './AdminCreateUserPage.scss';
import ErrorResponse from 'src/models/ErrorResponse';

interface IAdminCreateUserPageStateProps {
    userRoles: Array<UserRole>;
    offices: Array<Office>;
    users: PagedResultSet<User>;
    allUsers: Array<User>;
    selectedUser: User;
    duplicateUser: boolean;
    errorMessage: string;
}

interface IAdminCreateUserPageDispatchProps {
    navigate: (path: string) => void;
    createUser: (user: User) => void;
    editUser: (user: User, onComplete?: (error?: ErrorResponse) => void) => void;
    getRoles: () => void;
    getFieldOffices: () => void;
    getUsers: (resetPage: boolean) => void;
    getUserById: (id: number) => void;
    initUsers: () => void;
}

type IAdminCreateUserPageProps = RouteComponentProps<{ userId?: string }> & IAdminCreateUserPageDispatchProps & IAdminCreateUserPageStateProps;

interface IAdminCreateUserPageState {
    selectedRoleIds: number[];
    roleError: string;
}

interface IUserRoleCheckboxesProps {
    roles: UserRole[];
    selectedRoleIds: number[];
    error: string;
    toggleRole: (roleId: number) => void;
}

const UserRoleCheckboxes = (props: IUserRoleCheckboxesProps) => (
    <fieldset className={`user-role-fieldset ${props.error ? 'error' : ''}`}>
        <legend>Roles</legend>
        <p>Select one or more roles. Roles can also be managed from the Roles tab.</p>
        <div className="user-role-checkboxes">
            {props.roles.map((role) => (
                <label key={role.id}>
                    <input
                        type="checkbox"
                        checked={props.selectedRoleIds.includes(role.id)}
                        onChange={() => props.toggleRole(role.id)}
                    />{' '}
                    {role.role}
                </label>
            ))}
        </div>
        {props.error && <div className="user-role-error">{props.error}</div>}
    </fieldset>
);
UserRoleCheckboxes.displayName = 'UserRoleCheckboxes';

export class AdminCreateUserPage extends React.Component<IAdminCreateUserPageProps, IAdminCreateUserPageState> {
    private userId?: number;
    private buttonText?: string;
    private cardTitle?: string;

    public state: IAdminCreateUserPageState = {
        selectedRoleIds: [],
        roleError: '',
    };

    public componentDidMount() {
        this.userId = parseInt(this.props.match.params.userId, 10);
        this.props.getRoles();
        this.props.getFieldOffices();
        this.props.getUsers(true);
        if (this.userId && this.userId > 0) {
            this.props.getUserById(this.userId);
            this.buttonText = 'Save';
            this.cardTitle = 'Edit User';
        } else {
            this.buttonText = 'Create User';
            this.cardTitle = 'Create User';
        }
        this.initializeRoles(this.props.selectedUser);
    }

    public componentWillUnmount() {
        this.props.initUsers();
    }

    public componentDidUpdate(previousProps: IAdminCreateUserPageProps) {
        if (this.props.selectedUser && this.props.selectedUser !== previousProps.selectedUser) {
            this.initializeRoles(this.props.selectedUser);
        }
    }

    initializeRoles = (user: User) => {
        if (!user) {
            return;
        }
        const assignedRoles = user.roles && user.roles.length > 0 ? user.roles : user.role ? [user.role] : [];
        this.setState({ selectedRoleIds: assignedRoles.map((role) => role.id), roleError: '' });
    };

    validate = (user: User) => {
        const errors: FormErrors<User> = {};
        errors.office = {};
        errors.manager = {};

        if (user.username === undefined) {
            errors.username = 'Email address required';
        }

        if (user.firstName === undefined) {
            errors.firstName = 'First name is required';
        }

        if (user.lastName === undefined) {
            errors.lastName = 'Last name is required';
        }

        if (user.office === undefined) {
            errors.office.id = 'Office is required';
        }

        if (user.manager === undefined) {
            errors.manager.id = 'Manager is required';
        }

        return errors as FormErrors<AnyObject>;
    };

    createUser = (user: User) => {
        if (this.state.selectedRoleIds.length === 0) {
            this.setState({ roleError: 'At least one role is required' });
            return;
        }
        user.roles = this.props.userRoles.filter((role) => this.state.selectedRoleIds.includes(role.id));
        user.role = user.role && this.state.selectedRoleIds.includes(user.role.id) ? user.role : user.roles[0];
        if (user.office?.id > 0) {
            user.office = this.props.offices.find((o) => o.id === parseInt(user.office.id + '', 10));
        }
        if (user.manager?.id > 0) {
            user.manager = this.props.allUsers.find((u) => u.id === parseInt(user.manager.id + '', 10));
        }

        if (this.userId && this.userId > 0) {
            this.props.editUser(user, (error?: ErrorResponse) => {
                if (error) {
                    this.setState({ roleError: error.message || 'Unable to save user roles.' });
                }
            });
        } else {
            user.type = { id: UserTypes.Employee, name: '' };
            this.props.createUser(user);
        }
    };

    toggleRole = (roleId: number) => {
        this.setState((state) => ({
            selectedRoleIds: state.selectedRoleIds.includes(roleId)
                ? state.selectedRoleIds.filter((id) => id !== roleId)
                : [...state.selectedRoleIds, roleId],
            roleError: '',
        }));
    };

    cancel = () => {
        this.props.navigate('/admin/users');
    };

    public render() {
        const availableRoles = Array.isArray(this.props.userRoles)
            ? this.props.userRoles.filter((role) => role.isActive !== false || this.state.selectedRoleIds.includes(role.id))
            : [];
        const officeItems = this.props.offices ? this.props.offices.map((office: Office) => new SelectItem(office.id, office.name)) : undefined;
        const userItems =
            this.props.allUsers && this.props.allUsers
                ? this.props.allUsers
                      .filter((u) => u.role?.id === UserRoles.ProspectManager || u.roles?.some((role) => role.id === UserRoles.ProspectManager))
                      .map((user: User) => new SelectItem(user.id, `${user.firstName} ${user.lastName}`))
                : undefined;

        return (
            <CardSection title={this.cardTitle}>
                {this.props.duplicateUser && <div style={{ color: 'red', height: '3rem' }}>{this.props.errorMessage}</div>}
                <Form onSubmit={this.createUser} validate={this.validate} initialValues={this.props.selectedUser}>
                    <FormTextbox label="Email" name="username" />
                    <FormTextbox label="First Name" name="firstName" />
                    <FormTextbox label="Last Name" name="lastName" />
                    <FormTextBox label="Default Pay Rate" name="defaultPayRate" type={FormTextBoxDataType.Float} />
                    <UserRoleCheckboxes
                        roles={availableRoles}
                        selectedRoleIds={this.state.selectedRoleIds}
                        error={this.state.roleError}
                        toggleRole={this.toggleRole}
                    />
                    <FormSelect label="Office" name="office.id" items={officeItems} />
                    <FormSelect label="Manager" name="manager.id" items={userItems} />
                    <div className="admin-user-form-actions">
                        <Button text="Cancel" type="button" onClick={this.cancel} />
                        <Button text={this.buttonText} type="submit" intent="primary" />
                    </div>
                </Form>
            </CardSection>
        );
    }
}

function mapStateToProps(state: IRootState) {
    return {
        userRoles: state.user.roles,
        offices: selectors.office.offices(state),
        allUsers: state.user.allUsers,
        selectedUser: state.user.selectedUser,
        duplicateUser: state.user.duplicateUser,
        errorMessage: state.user.errorMessage,
    };
}

const mapDispatchToProps: IAdminCreateUserPageDispatchProps = {
    navigate: (path: string) => replace(path),
    createUser: (user: User) => actions.user.createUser(user),
    editUser: (user: User, onComplete?: (error?: ErrorResponse) => void) => actions.user.editUser(user, onComplete),
    getRoles: () => actions.user.getRoles(),
    getFieldOffices: () => actions.office.getOffices(),
    getUsers: (resetPage: boolean) => actions.user.getUsers(resetPage),
    getUserById: (id: number) => actions.user.getUserById(id),
    initUsers: () => actions.user.initUsers(),
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminCreateUserPage);



// WEBPACK FOOTER //
// ./src/components/admin/sections/AdminCreateUserPage.tsx